import { ReactNode } from "react";

import { Button } from "@buildpy/ui/button";
import { useLeaveGuard } from "@/hooks/use-leave-guard";
import { cn } from "@/lib/utils";

export const UNSAVED_LEAVE_CONFIRM = "저장하지 않은 변경이 있습니다 — 저장하지 않고 나가면 사라집니다. 그대로 나갈까요?";

interface Props {
  // 저장하지 않은 변경이 있을 때만 누를 수 있다.
  dirty: boolean;
  pending: boolean;
  // 없으면 감싼 form 을 제출한다(type="submit").
  onSave?: () => void;
  // 버튼 글자 — 기본 「저장」. 고친 줄 수를 붙일 때 쓴다.
  label?: string;
  // 저장을 막는 사유 — 있으면 버튼을 잠그고 빨간 글로 보인다.
  blocked?: ReactNode;
  // 새로 만들기 화면 — 고친 게 없어도 눌러 검증 안내를 볼 수 있다(떠나기 확인은 dirty 그대로).
  alwaysEnabled?: boolean;
  className?: string;
}

// 설정 화면 맨 아래에 붙는 저장 줄 — 탭마다 한 줄, 화면을 내려도 따라오고(홈 표시줄만큼 띄운다), 고친 게 남은 채 떠나면(탭·링크·새로고침·뒤로가기) 묻는다.
const SaveBar = ({ dirty, pending, onSave, label = "저장", blocked, alwaysEnabled = false, className }: Props) => {
  useLeaveGuard({ unload: dirty, links: dirty, back: dirty, message: UNSAVED_LEAVE_CONFIRM });

  return (
    <div
      className={cn(
        "sticky bottom-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border bg-background pb-safe-3 pt-3",
        className
      )}
      data-testid="save-bar">
      <Button
        className="shrink-0"
        disabled={pending || (!dirty && !alwaysEnabled) || Boolean(blocked)}
        onClick={onSave}
        size="sm"
        type={onSave ? "button" : "submit"}>
        {pending ? "저장 중…" : label}
      </Button>
      {/* 상태 문구는 늘 있는 live 영역 안에서 바뀐다 — 화면 읽기에 「저장 중」·「변경 있음」 이 들린다 */}
      <span aria-live="polite" className="text-sm" data-testid="save-status">
        {pending ? (
          <span className="sr-only">저장 중…</span>
        ) : blocked ? (
          <span className="text-destructive" data-testid="save-blocked">
            {blocked}
          </span>
        ) : (
          dirty && <span className="text-muted-foreground">저장하지 않은 변경이 있습니다.</span>
        )}
      </span>
    </div>
  );
};

export default SaveBar;

// 저장 바 없이 바꾸는 순간 저장되는 칸(스위치·선택·추가 버튼) 옆에 붙이는 표시.
export const InstantSaveNote = ({ children = "바꾸면 바로 저장됩니다" }: { children?: ReactNode }) => (
  <span className="text-xs text-muted-foreground" data-testid="instant-save">
    {children}
  </span>
);
